<template>
    <section
        id="pricing"
        class="border-y border-slate-200 bg-slate-50"
        aria-labelledby="pricing-heading"
    >
        <div class="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
            <div class="text-center">
                <p class="text-sm font-bold text-slate-600">No hidden fees</p>
                <h2
                    id="pricing-heading"
                    class="mt-2 text-3xl font-bold tracking-tight"
                >
                    Simple pricing for solo experts
                </h2>
                <p class="mt-3 text-slate-600">
                    Start free and upgrade when you're ready. Both plans include
                    the core tools to create services, get booked, and run
                    client sessions smoothly.
                </p>
            </div>

            <ul class="max-w-4xl mx-auto mt-12 grid grid-cols-8 gap-6">
                <li
                    v-for="plan in plans"
                    :key="plan.id.value"
                    class="col-span-8 md:col-span-4"
                >
                    <article
                        :class="[
                            'relative h-full rounded-xl bg-white p-8 shadow-xs shadow-slate-200/70',
                            plan.highlight
                                ? 'border-2 border-primary'
                                : 'border border-slate-200',
                        ]"
                        :aria-labelledby="`pricing-plan-${plan.id.value}-heading`"
                    >
                        <p
                            v-if="plan.highlight"
                            class="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-bold text-white"
                        >
                            Most popular
                        </p>

                        <h3
                            :id="`pricing-plan-${plan.id.value}-heading`"
                            class="text-lg font-bold"
                        >
                            {{ plan.name }}
                        </h3>
                        <p
                            v-if="plan.description !== null"
                            class="mt-1 text-sm text-slate-600"
                        >
                            {{ plan.description }}
                        </p>

                        <p class="mt-6 flex items-end gap-2">
                            <span
                                class="text-4xl font-bold"
                                :aria-label="`$${plan.price} per ${plan.periodLabel}`"
                            >
                                ${{ plan.price }}
                            </span>
                            <span
                                class="pb-1 text-slate-600"
                                aria-hidden="true"
                            >
                                /{{ plan.periodLabel }}
                            </span>
                        </p>

                        <p class="mt-6">
                            <a
                                :href="authRegisterUrl"
                                :class="[
                                    'inline-flex w-full items-center justify-center rounded-lg2 px-4 py-3 text-sm font-bold',
                                    plan.highlight
                                        ? 'bg-primary text-white hover:bg-primary-shade'
                                        : 'border border-slate-300 bg-slate-50 text-slate-700 hover:bg-white',
                                ]"
                            >
                                Get started
                            </a>
                        </p>

                        <ul class="mt-6 space-y-3 text-sm text-slate-700">
                            <li
                                v-for="feature in plan.featuresDisplay"
                                :key="feature.title"
                                class="flex gap-2"
                            >
                                <span
                                    :class="{
                                        'text-emerald-600': feature.enabled,
                                        invisible: !feature.enabled,
                                    }"
                                    aria-hidden="true"
                                >
                                    ✓
                                </span>
                                <span
                                    :class="{
                                        'line-through': !feature.enabled,
                                    }"
                                >
                                    {{ feature.title }}
                                </span>
                            </li>
                        </ul>
                    </article>
                </li>
            </ul>
        </div>
    </section>
</template>

<script setup lang="ts">
    import Plan from '@/App/Plan/Entity/Plan'

    defineProps({
        authRegisterUrl: {
            type: String,
            required: true,
        },
        plans: {
            type: Array as () => Plan[],
            required: true,
        },
    })
</script>
